import { EventEmitter } from 'eventemitter3';
import { TUNING, type Side } from '../config/tuning';

export type ChokeWarning = 'safe' | 'mild' | 'heavy';

export class ChokeController extends EventEmitter {
  static EVT_CHANGED = 'choke:changed';
  static EVT_WARNING_LEVEL = 'choke:warning';
  static EVT_FORCED = 'choke:forced';
  static EVT_REDUCED = 'choke:reduced';

  private meter: Record<Side, number> = { player: 0, ai: 0 };
  private warnLevel: Record<Side, ChokeWarning> = { player: 'safe', ai: 'safe' };

  reset(side?: Side): void {
    if (side) {
      this.meter[side] = 0;
      this.updateWarning(side);
      this.emit(ChokeController.EVT_CHANGED, side, 0);
    } else {
      this.meter.player = 0;
      this.meter.ai = 0;
      this.updateWarning('player');
      this.updateWarning('ai');
      this.emit(ChokeController.EVT_CHANGED, 'player', 0);
      this.emit(ChokeController.EVT_CHANGED, 'ai', 0);
    }
  }

  /** Returns true if rolled a forced choke or hit 100%. */
  tickInhale(side: Side, deltaSeconds: number): boolean {
    const prev = this.meter[side];
    const next = Math.min(100, prev + TUNING.choke.risePerSec * deltaSeconds);
    this.meter[side] = next;
    this.emit(ChokeController.EVT_CHANGED, side, next);
    this.updateWarning(side);

    if (next >= TUNING.choke.threshold.forced) {
      this.emit(ChokeController.EVT_FORCED, side);
      return true;
    }

    const probPerSec = this.chokeProbabilityPerSec(next);
    if (probPerSec > 0) {
      const chance = 1 - Math.pow(1 - probPerSec / 100, deltaSeconds);
      if (Math.random() < chance) {
        this.emit(ChokeController.EVT_FORCED, side);
        return true;
      }
    }
    return false;
  }

  private chokeProbabilityPerSec(meter: number): number {
    const t = TUNING.choke.threshold;
    if (meter < t.none) return 0;
    if (meter < t.mildWarning) {
      const span = t.mildWarning - t.none;
      const ratio = (meter - t.none) / span;
      return TUNING.choke.probabilityPerSec.mild.prob[0]
        + (TUNING.choke.probabilityPerSec.mild.prob[1] - TUNING.choke.probabilityPerSec.mild.prob[0]) * ratio;
    }
    if (meter < t.heavyWarning) {
      const span = t.heavyWarning - t.mildWarning;
      const ratio = (meter - t.mildWarning) / span;
      return TUNING.choke.probabilityPerSec.moderate.prob[0]
        + (TUNING.choke.probabilityPerSec.moderate.prob[1] - TUNING.choke.probabilityPerSec.moderate.prob[0]) * ratio;
    }
    // Severe band (90-99%): quadratic ramp — entry barely above moderate, top
    // is near-certain choke within a second.
    const span = 99 - t.heavyWarning;
    const ratio = Math.min(1, (meter - t.heavyWarning) / span);
    return TUNING.choke.probabilityPerSec.severe.prob[0]
      + (TUNING.choke.probabilityPerSec.severe.prob[1] - TUNING.choke.probabilityPerSec.severe.prob[0]) * ratio * ratio;
  }

  private updateWarning(side: Side): void {
    const m = this.meter[side];
    const t = TUNING.choke.threshold;
    let level: ChokeWarning = 'safe';
    if (m >= t.heavyWarning) level = 'heavy';
    else if (m >= t.mildWarning) level = 'mild';
    if (level !== this.warnLevel[side]) {
      this.warnLevel[side] = level;
      this.emit(ChokeController.EVT_WARNING_LEVEL, side, level);
    }
  }

  /**
   * Decrease the choke meter by up to `pct` (0..100). Clamped at 0. Emits EVT_CHANGED
   * and EVT_REDUCED so consumers can show a positive-feedback cue. Recalculates the
   * warning tier so pulse anims stop if the meter drops below the threshold.
   */
  reduce(side: Side, pct: number): number {
    const prev = this.meter[side];
    const next = Math.max(0, prev - Math.max(0, pct));
    if (next === prev) return 0;
    this.meter[side] = next;
    this.emit(ChokeController.EVT_CHANGED, side, next);
    this.emit(ChokeController.EVT_REDUCED, side, prev - next);
    this.updateWarning(side);
    return prev - next;
  }

  get(side: Side): number {
    return this.meter[side];
  }

  getWarning(side: Side): ChokeWarning {
    return this.warnLevel[side];
  }
}
